import React, { useState } from "react";
import { LibraryBig } from "lucide-react";
import { toast } from "sonner";

import { useKnowledgeBases } from "@/shared/api/knowledge";
import { useSetThreadKnowledge } from "@/shared/api/threadKnowledge";
import { useThread } from "@/shared/api/threads";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Switch } from "@/shared/components/ui/switch";
import { BrandButton } from "@/shared/components/ui-kit";

import type { KnowledgeBase } from "@/shared/types";

/* The composer strip's knowledge link for one chat: what it reads now, and the way to
   change that for this chat only. The agent's own defaults live in its Settings. */

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong");

function Picker({ choices, current, saving, onSave, onClose }: {
  choices: KnowledgeBase[];
  current: string[];
  saving: boolean;
  onSave: (ids: string[]) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState(current);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[440px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            Knowledge for this chat
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
            Only these bases are searched in this chat. Applies immediately.
          </DialogDescription>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {choices.length === 0 && (
            <div className="px-5 py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">No knowledge bases yet</div>
          )}
          {choices.map((b) => (
            <label key={b.id} className="flex items-center gap-2.5 px-5 py-2 cursor-pointer hover:bg-[var(--surface-sunk)]/45">
              <input type="checkbox" checked={picked.includes(b.id)} onChange={() => toggle(b.id)} />
              <span title={b.name} className="min-w-0 flex-1 text-[13px] font-medium text-[var(--text-primary)] truncate">{b.name}</span>
              <span className="shrink-0 whitespace-nowrap text-[11.5px] text-[var(--text-tertiary)]">
                {b.file_count} {b.file_count === 1 ? "file" : "files"}
              </span>
            </label>
          ))}
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <BrandButton onClick={() => onSave(picked)} disabled={saving}>Save</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ChatKnowledge({ agentId, chatId }: {
  agentId: string;
  /** None before the first message: the thread does not exist yet. */
  chatId?: string;
}) {
  const { data: thread } = useThread(chatId ?? null);
  const { data: agentBases = [] } = useKnowledgeBases(agentId);
  const { data: allBases = [] } = useKnowledgeBases();
  const save = useSetThreadKnowledge();
  const [picking, setPicking] = useState(false);

  const knowledge = thread?.knowledge;
  const overridden = !!knowledge?.overridden;
  const bases = knowledge?.knowledge_bases ?? [];
  const on = !overridden || bases.length > 0;
  const agentRefs = agentBases.map(({ id, name }) => ({ id, name }));

  const choose = (ids: string[] | null, shown: { id: string; name: string }[], done?: () => void) =>
    save.mutate(
      { threadId: chatId!, ids, bases: shown },
      { onSuccess: done, onError: (e) => toast.error(errorText(e)) },
    );
  const pick = (ids: string[]) => choose(ids, allBases.filter((b) => ids.includes(b.id)), () => setPicking(false));

  let body: React.ReactNode;
  if (!chatId) {
    body = <div className="text-[12.5px] text-[var(--text-quaternary)] py-1 italic">Available after the first message</div>;
  } else if (!knowledge) {
    body = <div className="text-[12.5px] text-[var(--text-quaternary)] py-1">Loading knowledge…</div>;
  } else if (!on) {
    body = <div className="text-[12.5px] text-[var(--text-tertiary)] py-1">Knowledge is off for this chat.</div>;
  } else {
    body = (
      <>
        <div className="section-label pb-1">
          {overridden ? "Custom for this chat" : "From agent"}
        </div>
        {bases.length === 0 && (
          <div className="text-[12.5px] text-[var(--text-tertiary)] py-1">
            This agent has no knowledge bases.{" "}
            <a href={`#/settings/${agentId}/knowledge`} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] underline underline-offset-2">
              Add some in Settings
            </a>
          </div>
        )}
        {bases.map((k) => (
          <div key={k.id} className="flex items-center gap-2.5 rounded-[8px] px-2 py-1.5 hover:bg-[var(--surface)] min-w-0">
            <div className="w-[30px] h-[30px] shrink-0 rounded-[7px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
              <LibraryBig className="w-3.5 h-3.5 text-[var(--text-secondary)]" strokeWidth={1.75} />
            </div>
            <div title={k.name} className="min-w-0 flex-1 text-[13px] font-medium text-[var(--text-primary)] truncate tracking-[-0.005em]">{k.name}</div>
          </div>
        ))}
      </>
    );
  }

  const label = knowledge && !on ? "Off" : knowledge ? String(bases.length) : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          title="Knowledge for this chat"
          aria-label={`Knowledge for this chat${label ? `: ${label}` : ""}`}
          className="press h-[26px] px-1.5 rounded-[8px] text-[11.5px] flex items-center gap-1.5 whitespace-nowrap hover:bg-[var(--accent-hover)] data-[state=open]:bg-[var(--accent-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] shrink-0"
        >
          <LibraryBig className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
          Knowledge
          {label && <span className="font-mono tabular-nums text-[var(--text-tertiary)]">{label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[300px] p-0 rounded-[12px] bg-[var(--canvas)] border-[var(--hairline-strong)]"
        // the picker is a dialog of its own: working in it must not close this
        onInteractOutside={(e) => picking && e.preventDefault()}
        onFocusOutside={(e) => picking && e.preventDefault()}
      >
        <div className="px-4 py-3 border-b hairline flex items-center gap-2">
          <span className="flex-1 text-[13px] font-medium text-[var(--text-primary)]">Knowledge for this chat</span>
          {knowledge && (
            <Switch
              aria-label="Knowledge for this chat"
              checked={on}
              onCheckedChange={() => (on ? choose([], []) : choose(null, agentRefs))}
            />
          )}
        </div>
        <div className="px-4 py-3 space-y-1">
          {body}
          {knowledge && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 text-[12.5px]">
              <button onClick={() => setPicking(true)} className="press whitespace-nowrap font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                Change
              </button>
              {overridden && (
                <button onClick={() => choose(null, agentRefs)} className="press whitespace-nowrap text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                  Reset to agent defaults
                </button>
              )}
              <span className="ml-auto whitespace-nowrap text-[11.5px] text-[var(--text-quaternary)]">Applies immediately</span>
            </div>
          )}
        </div>
      </PopoverContent>
      {picking && (
        <Picker
          choices={allBases}
          current={bases.map((b) => b.id)}
          saving={save.isPending}
          onSave={pick}
          onClose={() => setPicking(false)}
        />
      )}
    </Popover>
  );
}
